import React from "react";
import { BottomSheet, Button, Dialog, MenuItem, Text } from "@/components/DesignSystem";
import { DesktopOnly } from "@/components/shared/DesktopOnly";
import { MobileOnly } from "@/components/shared/MobileOnly";
import { Stack, SxProps } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useTranslation } from 'react-i18next';

type ConfirmDialogProps = {
  cancelText?: string,
  children?: React.ReactNode,
  confirmText?: string,
  /**
   * Blocks every route out of the dialog - the cancel action, the close button, backdrop
   * clicks, Escape, and mobile swipe-to-dismiss. Use while an operation must run to
   * completion before the user can leave.
   */
  disableClose?: boolean,
  isConfirmDisabled?: boolean,
  isLoading?: boolean,
  onCancel: () => void,
  onConfirm?: () => void,
  open: boolean,
  showCloseButton?: boolean,
  testID: string,
  title?: string,
  sx?: SxProps,
}

export const ConfirmDialog = ({
  cancelText,
  children,
  confirmText,
  disableClose = false,
  isConfirmDisabled = false,
  isLoading = false,
  onCancel,
  onConfirm,
  open,
  showCloseButton,
  testID,
  title,
  sx,
}: ConfirmDialogProps) => {
  const { t } = useTranslation();
  const handleCancel = () => {
    if (disableClose) {
      return;
    }
    onCancel();
  };
  return (
    <>
      <MobileOnly>
        <BottomSheet
          onClose={handleCancel}
          onOpen={() => { /* nothing to do, but onOpen is required */ }}
          open={open}
          sx={sx}
          testID={testID}
        >
          <Stack alignItems="start">
            <Stack padding="0px 0px 12px 16px">
              <Text category="title-large" testID={`${testID}-title`}>{title}</Text>
            </Stack>
            <Stack padding="0px 0px 12px 16px">
              {children}
            </Stack>
            {!!onConfirm && (
              <MenuItem
                color="primary"
                disabled={isConfirmDisabled}
                onClick={onConfirm}
                testID={`${testID}-confirm`}
              >
                {confirmText || t($ => $.common.actions.confirm)}
              </MenuItem>
            )}
            <MenuItem
              disabled={disableClose}
              onClick={handleCancel}
              testID={`${testID}-cancel`}
            >
              {cancelText || t($ => $.common.actions.cancel)}
            </MenuItem>
          </Stack>
        </BottomSheet>
      </MobileOnly>
      <DesktopOnly>
        <Dialog
          acceptButton={() => (
            <>
              {!!onConfirm && (
                <Button
                  disabled={isConfirmDisabled}
                  loading={isLoading}
                  onClick={onConfirm}
                  testID={`${testID}-confirm`}
                  variant="contained"
                >
                  {confirmText  || t($ => $.common.actions.confirm)}
                </Button>
              )}
            </>
          )}
          cancelButton={() => (
            <Button
              disabled={isLoading || disableClose}
              onClick={handleCancel}
              startIcon={<ArrowBackIcon />}
              testID={`${testID}-cancel`}
              variant="text"
            >
              {cancelText || t($ => $.common.actions.cancel)}
            </Button>
          )}
          onClose={handleCancel}
          open={open}
          showCloseButton={showCloseButton}
          sx={sx}
          testID={testID}
          title={title}
        >
          {children}
        </Dialog>
      </DesktopOnly>
    </>
  );
};
